import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Bell, BellOff } from "lucide-react";
import { toast } from "sonner";
import { setNotifPrefs } from "@/lib/notifPrefs";
import { showAppNotification, subscribeToPush } from "@/lib/notify";
import api from "@/lib/api";

const LS_KEY = "hw_notif_asked_v1";

export default function NotificationPermissionPrompt() {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!("Notification" in window)) return;
    if (localStorage.getItem(LS_KEY)) return;
    if (Notification.permission !== "default") {
      localStorage.setItem(LS_KEY, "already");
      return;
    }
    // Small delay so it doesn't hit the user immediately on login
    const t = setTimeout(() => setOpen(true), 900);
    return () => clearTimeout(t);
  }, []);

  const allow = async () => {
    try {
      const res = await Notification.requestPermission();
      localStorage.setItem(LS_KEY, res);
      if (res === "granted") {
        setNotifPrefs({ browser: true });
        toast.success("Notifiche attivate");
        showAppNotification("Hi-Work", "Le notifiche sono attive.");
        subscribeToPush(api);
      } else {
        toast("Notifiche disattivate", { description: "Le puoi attivare più tardi dalle impostazioni del browser." });
      }
    } catch {
      /* ignore */
    } finally {
      setOpen(false);
    }
  };

  const decline = () => {
    localStorage.setItem(LS_KEY, "declined");
    setOpen(false);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent
        data-testid="notif-permission-modal"
        className="tp-glass-strong border-white/10 text-white max-w-md"
      >
        <DialogHeader>
          <div className="mx-auto h-14 w-14 rounded-2xl tp-brand-gradient shadow-lg shadow-fuchsia-600/40 flex items-center justify-center mb-2">
            <Bell size={26} className="text-white" />
          </div>
          <DialogTitle className="text-white text-xl text-center">
            Attiva le notifiche
          </DialogTitle>
          <DialogDescription className="text-zinc-400 text-sm text-center">
            Ti avvisiamo sul desktop quando una task viene completata o quando
            qualcuno ti assegna un lavoro. Puoi disattivarle in qualsiasi
            momento.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-2 pt-2">
          <Button
            data-testid="notif-allow-button"
            onClick={allow}
            className="tp-brand-btn w-full h-11 rounded-xl"
          >
            <Bell size={14} className="mr-2" /> Consenti notifiche
          </Button>
          <Button
            data-testid="notif-decline-button"
            variant="ghost"
            onClick={decline}
            className="w-full text-zinc-400 hover:text-white hover:bg-white/10"
          >
            <BellOff size={14} className="mr-2" /> Non ora
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
